// src/Pages/GoDownDetail.jsx
import React, { useState, useEffect } from 'react';
import { Box, Button, Checkbox, TextField, IconButton, Typography, Paper, Stack, Grid} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { FaFileExcel } from 'react-icons/fa';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import DetailCard from '../components/DetailCard';
import LoadingOverlay from '../components/LoadingOverlay';

// const path = `http://192.168.1.248:3103/api`

const GoDownDetail = ({ loggedInUser, companyData, connectionString }) => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [GodownSearchTerm, setGodownSearchTerm] = useState('');
    const [resetFlag, setResetFlag] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(5);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [mode, setMode] = useState('search');
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const path = `${connectionString}/api`;

    useEffect(() => {
        fetchData();
        fetchTotalCount();
    }, [resetFlag, offset, limit, mode]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            let apiUrl = `${path}/godown/search?mode=${mode}&offset=${offset}&limit=${limit}`;
            if (mode === 'search') {
                if (resetFlag === 1) {
                    setGodownSearchTerm('');
                    setOffset(0);
                    setResetFlag(0);
                    setSelectedRows([]);
                    setSelectAll(false);
                } else {
                    apiUrl += `&GodownName=${GodownSearchTerm}`;
                }
                const response = await axios.get(apiUrl);
                if (Array.isArray(response.data)) {
                    setData(offset === 0 ? response.data : [...data, ...response.data]);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTotalCount = async () => {
        try {
            const response = await axios.get(`${path}/godown/count`);
            setTotalCount(response.data);
        } catch (error) {
            console.error('Error fetching total count:', error);
        }
    };

    const funExportData = () => {
        axios({ url: `${path}/godown/search?mode=export`, method: 'GET', responseType: 'blob', }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'exportedFileGodown.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setMode('search');
        }).catch(error => { console.error("Excel download error!", error); });
    };

    const funSearch = () => {
        setOffset(0);
        setMode('search');
        fetchData();
        fetchTotalCount();
    };

    const funReset = () => {
        setResetFlag(1);
        setMode('search');
    };

    const funShowMore = () => { setOffset(prevOffset => prevOffset + limit); };

    const funSelectAll = () => {
        setSelectAll(!selectAll);
        setSelectedRows(!selectAll ? data.map(item => item.GodownID) : []);
    };

    const funRowSelect = (id) => { setSelectedRows(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]); };

    const funDeleteSelectedRows = async () => {
        try {
            await axios.post(`${path}/godown/delete`, { objAryId: selectedRows });
            funReset();
        } catch (error) {
            console.error('Delete error:', error);
        }
    };

    const handleCancel = () => {
        setIsVisible(false);
        funReset();
    };

    const handleFilter = () => {
        setIsVisible(false);
        funSearch();
    };

    return (
        <>
            <LoadingOverlay isLoading={isLoading} message="Loading data..." />
            <Box p={2} display="flex" flexDirection="column" gap={2}>
            {/* Top Bar */}
            <Paper sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton color="primary" onClick={() => setIsVisible(p => !p)}>
                        <FilterListIcon />
                    </IconButton>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton component={Link} to="/main/GodownMaster" color="primary">
                            <AddIcon />
                        </IconButton>

                        <Checkbox checked={selectAll} onChange={funSelectAll} />

                        <IconButton color="error" onClick={funDeleteSelectedRows} disabled={selectedRows.length === 0} >
                            <DeleteIcon />
                        </IconButton>

                        <IconButton onClick={funExportData} color="success">
                            <FaFileExcel size={22} />
                        </IconButton>
                    </Stack>
                </Box>

                <Typography variant="body2" color="text.secondary">
                    {data.length}/{totalCount}
                </Typography>
            </Paper>

            {/* Filter Section */}
            {isVisible && (
                <Box display="flex" justifyContent="center" alignItems="center">
                    <Paper elevation={1} sx={{ p: 4, borderRadius: 3, bgcolor: '#f1f5f9', border: '1px solid #e5e7eb', backdropFilter: 'blur(2px)', }} >
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 3,
                                fontWeight: 600,
                                color: '#1f2937',
                                textAlign: 'left',
                                fontSize: '1.125rem',
                            }}
                        >
                            Filter Godown Records
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField fullWidth label="Godown Name" variant="outlined" size="small" value={GodownSearchTerm} onChange={(e) => setGodownSearchTerm(e.target.value)} InputLabelProps={{ sx: { color: '#6b7280', fontSize: '0.875rem' }, }} inputProps={{ style: { fontSize: '0.875rem' } }} />
                            </Grid>

                            <Grid item xs={12}>
                                <Stack direction="row" spacing={2} justifyContent="center">
                                    <Button onClick={handleFilter} variant="contained" disableElevation
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderRadius: 2,
                                            bgcolor: '#1877F2',
                                            '&:hover': { bgcolor: '#166fe5' },
                                            fontSize: '0.875rem',
                                            px: 4,
                                        }}
                                    >
                                        Search
                                    </Button>
                                    <Button onClick={handleCancel} variant="outlined"
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderRadius: 2,
                                            borderColor: '#DC2626',
                                            color: '#DC2626',
                                            fontSize: '0.875rem',
                                            px: 4,
                                            '&:hover': {
                                                borderColor: '#b91c1c',
                                                color: '#b91c1c',
                                                backgroundColor: '#fef2f2',
                                            },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>
            )}

            {/* Godown Cards */}
            {data.map((item, index) => (
                <DetailCard
                    key={item.GodownID}
                    id={item.GodownID}
                    title={item.GodownName}
                    avatarText={item.GodownName?.[0]}
                    subtitleList={[]} // Add more fields if needed
                    selected={selectedRows.includes(item.GodownID)}
                    onSelect={funRowSelect}
                    onEdit={(id) => navigate(`/main/editGodownMaster/${id}`)}
                    backgroundColor="#f1f5f9"
                />
            ))}
            {/* Show More Button */}
            {data.length < totalCount && (
                <Box textAlign="center" mt={1}>
                    <Button variant="outlined" onClick={funShowMore}> Show More </Button>
                </Box>
            )}
        </Box>
        </>
    );
};

export default GoDownDetail;